/**
 * Scripted, deterministic agent runs for the homepage VibeCompliance demo.
 * No LLM calls. No API cost. Pure local state.
 */

export type VibePersona =
  | "security"
  | "aml"
  | "privacy"
  | "gxp"
  | "risk"
  | "federal"
  | "audit"
  | "hr"
  | "ai-gov"
  | "healthcare";

export interface AgentRun {
  agent: string;
  result: string;
  ms: number;
}

export interface VibeScenario {
  id: VibePersona;
  label: string;
  /** i18n key (namespace `vibeHero.scenarios.<i18nKey>`) resolved at the call site via t(). */
  i18nKey: string;
  prompt: string;
  sources: string[];
  /** Condensed 3-line summary used by the homepage hero animation. */
  steps: string[];
  agentRuns: AgentRun[];
  artifact: {
    title: string;
    meta: string;
    rows: { label: string; value: string }[];
  };
}

export const VIBE_SCENARIOS: VibeScenario[] = [
  {
    id: "security",
    i18nKey: "security",
    label: "Security",
    prompt: "Hey Fibo, show me where our SOC 2 evidence is stale.",
    sources: ["Okta", "AWS", "GitHub", "Jira"],
    steps: [
      "Scoped 142 SOC 2 controls",
      "Flagged 37 stale evidence items",
      "Drafted auditor-ready pack",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Located 142 SOC 2 controls in scope.", ms: 240 },
      { agent: "Evidence Agent", result: "Pulled 1,284 artifacts from Okta, AWS, GitHub, Jira.", ms: 320 },
      { agent: "Freshness Agent", result: "Flagged 37 stale (>90d), 12 missing owners.", ms: 280 },
      { agent: "Control Tester", result: "Re-ran 18 automated tests. 3 failing.", ms: 360 },
      { agent: "Audit Agent", result: "Drafted remediation memo + auditor-ready pack.", ms: 300 },
    ],
    artifact: {
      title: "Stale Evidence Report",
      meta: "4 pages, PDF + CSV",
      rows: [
        { label: "Stale evidence items", value: "37" },
        { label: "Missing owners", value: "12" },
        { label: "Failing tests", value: "3" },
      ],
    },
  },
  {
    id: "aml",
    i18nKey: "aml",
    label: "AML / KYC",
    prompt: "Hey Fibo, review our KYC refresh backlog and flag high-risk customers.",
    sources: ["Core banking", "Sanctions list", "Case mgmt", "Drive"],
    steps: [
      "Reviewed 2,140 overdue refreshes",
      "Flagged 63 for enhanced due diligence",
      "Queued cases with rationale",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Pulled 2,140 customers past refresh date.", ms: 260 },
      { agent: "Screening Agent", result: "Re-screened against sanctions and PEP lists.", ms: 360 },
      { agent: "Risk Agent", result: "Scored risk. 63 meet enhanced due diligence triggers.", ms: 320 },
      { agent: "Case Agent", result: "Opened 63 cases with documented rationale.", ms: 300 },
      { agent: "Reviewer", result: "Routed to the MLRO for disposition.", ms: 240 },
    ],
    artifact: {
      title: "KYC Refresh Queue",
      meta: "Case queue + CSV",
      rows: [
        { label: "Overdue refreshes", value: "2,140" },
        { label: "Enhanced due diligence", value: "63" },
        { label: "Screening hits", value: "18" },
      ],
    },
  },
  {
    id: "privacy",
    i18nKey: "privacy",
    label: "Privacy",
    prompt: "Hey Fibo, draft a DPIA for our new HR analytics tool.",
    sources: ["Workday", "BigQuery", "Drive", "Confluence"],
    steps: [
      "Mapped data flows, 6 systems",
      "Found 7 risks, 2 high",
      "Drafted DPIA per Art. 35 GDPR",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Mapped data flows across 6 systems.", ms: 260 },
      { agent: "Privacy Agent", result: "Classified 14 data categories. 4 special-category.", ms: 340 },
      { agent: "Risk Agent", result: "Identified 7 risks. 2 high, 5 medium.", ms: 300 },
      { agent: "Policy Agent", result: "Drafted DPIA per Art. 35 GDPR.", ms: 380 },
      { agent: "Reviewer", result: "Routed to DPO for sign-off.", ms: 240 },
    ],
    artifact: {
      title: "DPIA — HR Analytics",
      meta: "12 pages, DOCX",
      rows: [
        { label: "Data categories", value: "14" },
        { label: "High risks", value: "2" },
        { label: "Mitigations proposed", value: "9" },
      ],
    },
  },
  {
    id: "gxp",
    i18nKey: "gxp",
    label: "GxP",
    prompt: "Hey Fibo, assess our LIMS change control against 21 CFR Part 11.",
    sources: ["Veeva", "LIMS", "Jira", "Drive"],
    steps: [
      "Traced 48 validated changes",
      "Found 6 signature gaps",
      "Drafted Part 11 gap report",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Traced 48 changes in the validated LIMS state.", ms: 260 },
      { agent: "Evidence Agent", result: "Collected IQ, OQ and PQ records per change.", ms: 340 },
      { agent: "Control Tester", result: "Tested audit trail and e-signature integrity.", ms: 320 },
      { agent: "Compliance Agent", result: "Found 6 gaps against Part 11 subparts B and C.", ms: 300 },
      { agent: "Reviewer", result: "Routed to Quality Assurance for CAPA.", ms: 240 },
    ],
    artifact: {
      title: "Part 11 Validation Gap Report",
      meta: "9 pages, PDF",
      rows: [
        { label: "Changes traced", value: "48" },
        { label: "Signature gaps", value: "6" },
        { label: "CAPAs proposed", value: "4" },
      ],
    },
  },
  {
    id: "risk",
    i18nKey: "risk",
    label: "Risk",
    prompt: "Hey Fibo, refresh our top-10 enterprise risks for the board.",
    sources: ["Jira", "ServiceNow", "Drive", "Slack"],
    steps: [
      "Loaded register, 84 risks",
      "Re-scored, 9 tier changes",
      "Wrote board exec summary",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Pulled current risk register (84 risks).", ms: 240 },
      { agent: "Signal Agent", result: "Ingested 3,200 tickets + 18 incidents.", ms: 360 },
      { agent: "Risk Agent", result: "Re-scored 84 risks. 9 changed tier.", ms: 320 },
      { agent: "Narrative Agent", result: "Wrote board-ready exec summary.", ms: 300 },
      { agent: "Reviewer", result: "Routed to CRO for approval.", ms: 220 },
    ],
    artifact: {
      title: "Board Risk Brief",
      meta: "6 pages, PDF",
      rows: [
        { label: "Risks re-scored", value: "84" },
        { label: "Tier changes", value: "9" },
        { label: "New emerging risks", value: "3" },
      ],
    },
  },
  {
    id: "federal",
    i18nKey: "federal",
    label: "Federal",
    prompt: "Hey Fibo, build our FedRAMP Moderate SSP control narratives.",
    sources: ["AWS GovCloud", "Okta", "GitHub", "Drive"],
    steps: [
      "Loaded 325 Moderate baseline controls",
      "Drafted 298 narratives from evidence",
      "Listed 27 for owner input",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Loaded the NIST 800-53 Moderate baseline (325 controls).", ms: 280 },
      { agent: "Evidence Agent", result: "Mapped GovCloud and Okta configuration to controls.", ms: 360 },
      { agent: "Narrative Agent", result: "Drafted 298 implementation statements with citations.", ms: 380 },
      { agent: "Gap Agent", result: "Flagged 27 controls needing owner input.", ms: 280 },
      { agent: "Reviewer", result: "Routed to the ISSO for review.", ms: 240 },
    ],
    artifact: {
      title: "SSP Narrative Package",
      meta: "OSCAL + DOCX",
      rows: [
        { label: "Controls in baseline", value: "325" },
        { label: "Narratives drafted", value: "298" },
        { label: "Needs owner input", value: "27" },
      ],
    },
  },
  {
    id: "audit",
    i18nKey: "audit",
    label: "Audit",
    prompt: "Hey Fibo, assemble the ISO 27001 evidence pack for our Q3 auditor.",
    sources: ["Drive", "GitHub", "Okta", "AWS"],
    steps: [
      "Loaded Annex A, 93 controls",
      "Linked evidence, 4 gaps",
      "Built auditor portal",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Loaded Annex A — 93 controls.", ms: 240 },
      { agent: "Evidence Agent", result: "Collected 1,612 artifacts across systems.", ms: 360 },
      { agent: "Mapping Agent", result: "Linked evidence to controls. 4 gaps.", ms: 300 },
      { agent: "Audit Agent", result: "Built auditor portal with citations.", ms: 340 },
      { agent: "Reviewer", result: "Shared read-only link with auditor.", ms: 240 },
    ],
    artifact: {
      title: "ISO 27001 Audit Pack",
      meta: "Auditor portal + ZIP",
      rows: [
        { label: "Controls covered", value: "89 / 93" },
        { label: "Evidence artifacts", value: "1,612" },
        { label: "Open gaps", value: "4" },
      ],
    },
  },
  {
    id: "hr",
    i18nKey: "hr",
    label: "HR",
    prompt: "Hey Fibo, check our workforce policies against the latest state leave laws.",
    sources: ["Workday", "Drive", "Confluence", "Slack"],
    steps: [
      "Checked 31 policies across 14 states",
      "Found 8 out of date",
      "Drafted redlines and notices",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Located 31 workforce policies across 14 states.", ms: 260 },
      { agent: "Regulatory Agent", result: "Tracked 11 leave law changes effective this year.", ms: 340 },
      { agent: "Gap Agent", result: "Found 8 policies out of date in 5 states.", ms: 300 },
      { agent: "Policy Agent", result: "Drafted redlines plus employee notice copy.", ms: 360 },
      { agent: "Reviewer", result: "Routed to HR and employment counsel.", ms: 240 },
    ],
    artifact: {
      title: "Policy Update Memo",
      meta: "8 pages, DOCX + redlines",
      rows: [
        { label: "Policies reviewed", value: "31" },
        { label: "Out of date", value: "8" },
        { label: "States affected", value: "5" },
      ],
    },
  },
  {
    id: "ai-gov",
    i18nKey: "aiGov",
    label: "AI Governance",
    prompt: "Hey Fibo, inventory our AI systems and classify them under the EU AI Act.",
    sources: ["GitHub", "Confluence", "Jira", "Drive"],
    steps: [
      "Found 42 AI systems",
      "Classified, 3 high-risk",
      "Drafted governance plan",
    ],
    agentRuns: [
      { agent: "Discovery Agent", result: "Found 42 AI systems across 9 teams.", ms: 320 },
      { agent: "Classifier", result: "Mapped to EU AI Act tiers. 3 high-risk.", ms: 360 },
      { agent: "Risk Agent", result: "Flagged 5 systems missing model cards.", ms: 280 },
      { agent: "Policy Agent", result: "Drafted governance plan per Art. 9.", ms: 340 },
      { agent: "Reviewer", result: "Routed to AI ethics lead.", ms: 240 },
    ],
    artifact: {
      title: "AI System Inventory",
      meta: "CSV + governance memo",
      rows: [
        { label: "AI systems discovered", value: "42" },
        { label: "High-risk (EU AI Act)", value: "3" },
        { label: "Missing model cards", value: "5" },
      ],
    },
  },
  {
    id: "healthcare",
    i18nKey: "healthcare",
    label: "Healthcare",
    prompt: "Hey Fibo, map our HIPAA safeguards to the HITRUST CSF.",
    sources: ["Epic", "Okta", "AWS", "Drive"],
    steps: [
      "Mapped 54 safeguards to HITRUST",
      "Found 11 partially met",
      "Built the crosswalk pack",
    ],
    agentRuns: [
      { agent: "Scoping Agent", result: "Loaded 54 HIPAA administrative and technical safeguards.", ms: 260 },
      { agent: "Mapping Agent", result: "Crosswalked to HITRUST CSF requirement statements.", ms: 360 },
      { agent: "Evidence Agent", result: "Attached evidence from Epic, Okta and AWS.", ms: 320 },
      { agent: "Gap Agent", result: "Marked 11 requirements partially met.", ms: 280 },
      { agent: "Reviewer", result: "Routed to the privacy and security officer.", ms: 240 },
    ],
    artifact: {
      title: "HIPAA to HITRUST Crosswalk",
      meta: "Crosswalk matrix + XLSX",
      rows: [
        { label: "Safeguards mapped", value: "54" },
        { label: "Partially met", value: "11" },
        { label: "Shared evidence reuse", value: "72%" },
      ],
    },
  },
];
